import React, { useRef, useState, useEffect } from "react";
import { Form, Input, Button, Upload, Tooltip, Spin, Select } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import userIcon from "../../assets/images/user-icon.svg";
import leftArrow from "../../assets/images/LeftArrow.svg";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import userServiceApi from "../../api/userServiceApi";
import "./addNewPartner.scss";
import { inputFieldCharLimit } from '../../config/index'
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { getRequest } from "../../api/http";

const toSubdomainSlug = (value) =>
  (value || "").toLowerCase().replace(/[^a-z0-9]/g, "");

const buildSubdomainName = (partnerName, siteName) => {
  const joined = [toSubdomainSlug(partnerName), toSubdomainSlug(siteName)]
    .filter(Boolean)
    .join("-");
  return joined.slice(0, inputFieldCharLimit).replace(/-$/, "");
};


const sanitizeSubdomainInput = (value) =>
  (value || "")
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "")
    .slice(0, inputFieldCharLimit);

const stripLeadingPlus = (value) => (value || "").replace(/^\++/, "");

const toApiMobileNumber = (value) => {
  const digits = stripLeadingPlus(value);
  return digits ? `+${digits}` : digits;
};

const AddPartnerForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [appLogoList, setAppLogoList] = useState([]);
  const [selectedAppLogo, setSelectedAppLogo] = useState(null);
  const [subdomainName, setSubdomainName] = useState("");
  const userEditedSubdomainRef = useRef(false);
  const isSyncingSubdomain = useRef(false);
  const [form] = Form.useForm();
  const [countries, setCountries] = useState([]);
  const [isBtnDisabled, setIsBtnDisabled] = useState(false);
  const { Option } = Select;


  const fetchStripeSupportedCountries = async () => {
    try {
      const resp = await getRequest(userServiceApi.stripeSupportedCountries());
      setCountries(resp.data);
    } catch (error) {
      console.log("error====>", error);
    }
  }
  useEffect(() => {
    fetchStripeSupportedCountries()
  }, [])

  const onFinish = async (values) => {
    console.log('values', values);
    setIsBtnDisabled(true);
    try {
      const token = sessionStorage.getItem("token");
      const formData = new FormData();

      if (fileList.length > 0) {
        formData.append("media", fileList[0].originFileObj);
      }

      if (appLogoList.length > 0) {
        formData.append("appLogo", appLogoList[0].originFileObj);
      }

      const inputObject = {
        name: values.name,
        email: values.email,
        countryId: values.country,
        siteName: values.siteName,
        addressInput: values.addressInput,
        mobileNumber: toApiMobileNumber(values.mobileNumber),
        // appName: values.appName,
        subDomainName: values.subdomainName,
      };

      formData.append(
        "input",
        new Blob([JSON.stringify(inputObject)], { type: "application/json" })
      );

      // --- Make API call ---
      const response = await fetch(userServiceApi.addPartner(), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      console.log("response status:", response.status);

      // 🧩 Handle 401 before reading JSON (since json() might throw)
      if (response.status === 401) {
        setIsBtnDisabled(false);
        toast.error("Session expired, please log in again.");
        sessionStorage.clear();
        navigate("/login");
        return;
      }

      // Parse JSON safely
      const data = await response.json();
      console.log("response data:", data);

      if (response.status === 200) {
        setLoading(true);
        toast.success("Partner added successfully");

        // Reset form fields
        form.resetFields();

        // Clear upload states
        setFileList([]);
        setSelectedFile(null);

        setAppLogoList([]);
        setSelectedAppLogo(null);

        setSubdomainName("");
        userEditedSubdomainRef.current = false;
        setIsBtnDisabled(false);
        navigate("/admin");
      }
      else if (response.status === 400) {
        const match = data.message?.match(/\{"errors":\[.*\]\}/);
        if (match) {
          const parsed = JSON.parse(match[0]);
          if (parsed.errors && Array.isArray(parsed.errors) && parsed.errors.length > 0) {
            toast.error(parsed.errors[0].message);
          }
        } else if (data?.errorLog || data.error) {
          toast.error(data.message || data.error);
        } else {
          toast.error(data.message || "Invalid request. Please check your details and try again.");
        }
      } else {
        toast.error(data?.message || "Unexpected error occurred. Please try again.");
      }

    } catch (error) {
      toast.error(error.message || "Something went wrong, please try again.");
    } finally {
      setTimeout(() => {
        setLoading(false);
        setIsBtnDisabled(false);
      }, 500)

    }
  };

  const customRequest = ({ file, onSuccess }) => {
    const localUrl = URL.createObjectURL(file);
    setFileList([{ uid: file.uid, name: file.name }]);
    setSelectedFile(localUrl);
    onSuccess();
  };

  const customAppLogoRequest = ({ file, onSuccess }) => {
    const localUrl = URL.createObjectURL(file);

    setAppLogoList([{ uid: file.uid, name: file.name }]);
    setSelectedAppLogo(localUrl);
    onSuccess();
  };

  const checkIfImagePassed = (file, size) => {
    if (!file?.type?.startsWith('image/')) {
      toast.error('Only image files are allowed')
      return false
    }
    if (file && file.size >= size) {
      toast.error("File size should not exceed 2MB");
      return false
    }
    return true
  }

  const onFileChange = (info) => {
    setFileList(info.fileList.slice(-1));
  };

  const onAppLogoFileChange = (info) => {
    setAppLogoList(info.fileList.slice(-1));
  };

  const syncSubdomainFromNames = (allValues) => {
    const nextSubdomain = buildSubdomainName(allValues?.name, allValues?.siteName);
    isSyncingSubdomain.current = true;
    form.setFieldsValue({ subdomainName: nextSubdomain });
    setSubdomainName(nextSubdomain);
    isSyncingSubdomain.current = false;
  };

  const handleValuesChange = (changedValues, allValues) => {
    if (changedValues.subdomainName !== undefined && !isSyncingSubdomain.current) {
      const autoValue = buildSubdomainName(allValues?.name, allValues?.siteName);
      const nextValue = changedValues.subdomainName || "";
      userEditedSubdomainRef.current =
        Boolean(nextValue) && nextValue !== autoValue;
      setSubdomainName(nextValue);
      return;
    }

    if (
      !userEditedSubdomainRef.current &&
      (changedValues.name !== undefined || changedValues.siteName !== undefined)
    ) {
      syncSubdomainFromNames(allValues);
    }
  };

  return (
    <div className="add-partner-container">
      <div
        className="heading-backBtn"
        onClick={() => navigate("/admin")}
      >
        <img style={{ marginRight: "8px" }} src={leftArrow} alt="leftarrow" />
        Add new Partner
      </div>
      <div className="main-container">
        <div className="multipalFormStyle">
          {loading ? (
            <Spin
              className="data-loader loading-svg"
              tip="Loading"
              size="large"
            />
          ) : (
            <Form
              className="addPartner"
              form={form}
              onFinish={onFinish}
              onValuesChange={handleValuesChange}
            >
              <div className="multipalFormRow">
                <div className="multipalFormCol">
                  <div className="formListRow">
                    <div className="formListCol">
                      <Form.Item
                        labelCol={{ span: 5 }}
                        label="Partner Name"
                        name="name"
                        rules={[
                          {
                            required: true,
                            message: "Please input the partner name!",
                          },
                        ]}
                      >
                        <Input placeholder="Partner Name" maxLength={inputFieldCharLimit} />
                      </Form.Item>

                      <Form.Item
                        labelCol={{ span: 5 }}
                        label="Email"
                        name="email"
                        normalize={(value) => value?.trim()}
                        rules={[
                          {
                            required: true,
                            message: "Please input the partner admin email!",
                          },
                          {
                            type: "email",
                            message: "Please enter a valid email address!",
                          },
                        ]}
                      >
                        <Input placeholder="Email" maxLength={inputFieldCharLimit} />
                      </Form.Item>

                      <Form.Item
                        labelCol={{ span: 5 }}
                        label="Mobile Number"
                        name="mobileNumber"
                        rules={[
                          {
                            required: true,
                            message: "Please enter a mobile number!",
                          },
                        ]}
                      >
                        <PhoneInput
                          country={"in"}
                          enableSearch
                          inputStyle={{
                            width: "100%",
                            height: "40px"
                          }}
                          onChange={(value) => {
                            form.setFieldsValue({
                              mobileNumber: value,
                            });
                          }}
                        />
                      </Form.Item>

                      <Form.Item
                        label="Country"
                        name="country"
                        labelCol={{ span: 5 }}
                        rules={[{
                          required: true,
                          message: "Please select your country",
                        },]} >
                        <Select
                          placeholder="Select country"
                          loading={loading}
                          showSearch
                          allowClear
                          optionFilterProp="label"
                          filterOption={(input, option) =>
                            String(option?.label || "")
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                          options={countries.map((country) => ({
                            value: country.id,
                            label: country.name,
                          }))}
                        />
                      </Form.Item>

                      <Form.Item
                        labelCol={{ span: 5 }}
                        label="Site Name"
                        name="siteName"
                        rules={[
                          { required: true, message: "Please input the site name!" },
                        ]}
                      >
                        <Input maxLength={25} showCount placeholder="Site Name" />
                      </Form.Item>

                      <Form.Item
                        labelCol={{ span: 5 }}
                        label="Address"
                        name={["addressInput", "street"]}
                        rules={[
                          {
                            required: true,
                            message: "Please input the street address!",
                          },
                        ]}
                      >
                        <div className="helpInfo">
                          <Input placeholder="Address" />
                          <Tooltip
                            title={"Please enter your full address including street, city, state, and postal code for accurate records."}
                          >
                            <QuestionCircleOutlined className="question" />
                          </Tooltip>
                        </div>
                      </Form.Item>

                      <Form.Item
                        labelCol={{ span: 5 }}
                        label="Logo"
                        name="logo"
                        valuePropName="fileList"
                        getValueFromEvent={(e) => e.fileList}
                        rules={[
                          {
                            required: true,
                            message: "Please upload the partner logo!",
                          },
                        ]}
                      >
                        <Upload
                          accept="image/*"
                          customRequest={customRequest}
                          beforeUpload={(file) => {
                            return checkIfImagePassed(file, 2e6)
                          }}
                          fileList={fileList}
                          onChange={onFileChange}
                          showUploadList={false}
                        >
                          {/* <Avatar
              shape="circle"
              size={64}
              src={selectedFile || userIcon}
              style={{ marginRight: 10, width: 50, height: 50 }}
              id="partner-avatar"
            /> */}
                          {selectedFile ? (
                            <div>
                              <img
                                src={selectedFile}
                                alt=""
                                style={{
                                  maxHeight: "120px",
                                  border: "1px solid #A5A5A5",
                                  borderRadius: "5px",
                                  objectFit: "contain",
                                  marginBottom: "-10px",
                                  marginRight: "10px",
                                }}
                              />
                              <Button
                                // style={{
                                //   backgroundColor: "green",
                                //   color: "white",
                                //   marginLeft: 10,
                                // }}
                                className="fileUploadBtn redBtn"
                              >
                                Upload Image
                              </Button>
                              <Tooltip
                                title={"Upload only .PNG, .JPG or .JPEG images and File size shouldn't exceed 2MB. Recommended image dimensions: 1280x720 pixels"}
                              >
                                <QuestionCircleOutlined className="question" />
                              </Tooltip>
                            </div>
                          ) : (
                            <div style={{ display: "flex", alignItems: "center" }}>
                              <img
                                src={userIcon}
                                alt=""
                                style={{ maxHeight: "60px", marginRight: "10px" }}
                              />
                              <Button
                                // style={{
                                //   backgroundColor: "green",
                                //   color: "white",
                                //   marginLeft: 10,
                                // }}
                                className="fileUploadBtn redBtn"
                              >
                                Upload Logo
                              </Button>
                              <Tooltip
                                title={"Upload only .PNG, .JPG or .JPEG images and File size shouldn't exceed 2MB. Recommended image dimensions: 1280x720 pixels"}
                              >
                                <QuestionCircleOutlined className="question" />
                              </Tooltip>
                            </div>
                          )}
                        </Upload>
                      </Form.Item>

                      <Form.Item
                        labelCol={{ span: 5 }}
                        label="Subdomain Name"
                        name="subdomainName"
                        normalize={sanitizeSubdomainInput}
                        rules={[
                          {
                            required: true,
                            message: "Please enter or generate the subdomain name!",
                          },
                        ]}
                      >
                        <Input
                          placeholder="Generated from partner name and site name"
                          maxLength={inputFieldCharLimit}
                        />
                      </Form.Item>

                      {subdomainName.length > 0 && (
                        <Form.Item
                          labelCol={{ span: 5 }}
                          className="subdomain-preview"
                          label=" "
                          name=""
                          colon={false}
                        >
                          <label
                            style={{ display: "block" }}
                          >{`Your end user URL would be ${subdomainName}.expo365.app and`}</label>
                          <label
                            style={{ display: "block" }}
                          >{`your organisation admin URL would be orgadmin.expo365.app/login/${subdomainName}`}</label>
                        </Form.Item>
                      )}

                      <Form.Item
                        labelCol={{ span: 5 }}
                        label="Site Logo"
                        name="appLogo"
                        valuePropName="fileList"
                        getValueFromEvent={(e) => e.fileList}
                        rules={[
                          { required: true, message: "Please upload the site logo!" },
                        ]}
                      >
                        <Upload
                          accept="image/*"
                          customRequest={customAppLogoRequest}
                          beforeUpload={(file) => {
                            return checkIfImagePassed(file, 2e6)
                          }}
                          fileList={appLogoList}
                          onChange={onAppLogoFileChange}
                          showUploadList={false}
                        >
                          {selectedAppLogo ? (
                            <div>
                              <img
                                alt="selectedAppLogo"
                                src={selectedAppLogo}
                                style={{
                                  maxHeight: "120px",
                                  border: "1px solid #A5A5A5",
                                  borderRadius: "5px",
                                  objectFit: "contain",
                                  marginBottom: "-10px",
                                  marginRight: "10px",
                                }}
                              />
                              <Button
                                className="fileUploadBtn redBtn"
                              >
                                Upload Site Logo
                              </Button>
                              <Tooltip
                                title={`Upload only .PNG, .JPG or .JPEG and File size shouldn't exceed 2MB. Recommended image dimensions: 1280x720 pixels`}
                              >
                                <QuestionCircleOutlined className="question" />
                              </Tooltip>
                            </div>
                          ) : (
                            <div style={{ display: "flex", alignItems: "center" }}>
                              <img
                                alt="userIcon"
                                src={userIcon}
                                style={{ maxHeight: "60px", marginRight: "10px" }}
                              />
                              <Button
                                className="fileUploadBtn redBtn"
                              >
                                Upload Site Logo
                              </Button>
                              <Tooltip
                                title={`Upload only .PNG, .JPG or .JPEG and File size shouldn't exceed 2MB. Recommended image dimensions: 1280x720 pixels`}
                              >
                                <QuestionCircleOutlined className="question" />
                              </Tooltip>
                            </div>
                          )}
                        </Upload>
                      </Form.Item>
                    </div>
                  </div>
                  <div className="formListRow">
                    <div className="formListCol" style={{ textAlign: 'right' }}>
                      <Form.Item>
                        <Button className="sendBtn redBtn" htmlType="submit" loading={isBtnDisabled}>
                          Submit
                        </Button>
                      </Form.Item>
                    </div>
                  </div>
                </div>
              </div>
            </Form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddPartnerForm;
